import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, GraduationCap, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/layout/dashboard-layout';

interface TeacherRequest {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  request_message: string;
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export default function BecomeTeacherPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [existingRequest, setExistingRequest] = useState<TeacherRequest | null>(null);
  const [isAlreadyTeacher, setIsAlreadyTeacher] = useState(false);

  useEffect(() => {
    loadRequestStatus();
  }, [user]);

  const loadRequestStatus = async () => {
    if (!user) return;

    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('is_teacher, role')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (profile?.is_teacher) {
        setIsAlreadyTeacher(true);
        setLoading(false);
        return;
      }

      const { data: requests, error: requestError } = await supabase
        .from('teacher_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (requestError && requestError.code !== 'PGRST116') throw requestError;

      if (requests) {
        setExistingRequest(requests);
      }
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare lo stato della richiesta',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast({
        title: 'Errore',
        description: 'Inserisci un messaggio per la richiesta',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('teacher_requests')
        .insert({
          user_id: user?.id,
          request_message: message.trim(),
          status: 'pending',
        });

      if (error) throw error;

      toast({
        title: 'Richiesta inviata',
        description: 'La tua richiesta è stata inviata con successo',
      });

      loadRequestStatus();
      setMessage('');
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile inviare la richiesta',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
            <Clock className="w-3 h-3 mr-1" />
            In attesa
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approvata
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            Rifiutata
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (isAlreadyTeacher) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-8">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Sei già un docente</CardTitle>
              <CardDescription>
                Il tuo account è già stato approvato come docente. Puoi creare e gestire i tuoi corsi.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/dashboard/my-courses')}>
                Vai ai miei corsi
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <GraduationCap className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Diventa Docente</h1>
          <p className="text-gray-600">
            Richiedi di diventare un docente sulla nostra piattaforma
          </p>
        </div>

        {existingRequest ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Stato della tua richiesta</CardTitle>
                {getStatusBadge(existingRequest.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Il tuo messaggio</Label>
                <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {existingRequest.request_message}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Data richiesta</Label>
                <p className="mt-1 text-gray-700">
                  {new Date(existingRequest.created_at).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>

              {existingRequest.status === 'rejected' && existingRequest.rejection_reason && (
                <div>
                  <Label className="text-sm font-medium text-red-700">Motivo del rifiuto</Label>
                  <p className="mt-1 text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
                    {existingRequest.rejection_reason}
                  </p>
                </div>
              )}

              {existingRequest.status === 'pending' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    La tua richiesta è in attesa di revisione da parte di un amministratore.
                    Ti invieremo una notifica quando verrà processata.
                  </p>
                </div>
              )}

              {existingRequest.status === 'rejected' && (
                <Button onClick={() => setExistingRequest(null)} className="w-full">
                  Invia una nuova richiesta
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Richiesta per diventare docente</CardTitle>
              <CardDescription>
                Spiega perché vuoi diventare un docente e quali competenze puoi condividere
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="message">
                    Messaggio <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Descrivi la tua esperienza, le materie che vorresti insegnare, le tue qualifiche..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    disabled={submitting}
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">
                    Minimo 50 caratteri. Sii specifico sulle tue competenze e motivazioni.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Cosa includere nella richiesta:</h4>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>La tua esperienza professionale o accademica</li>
                    <li>Le materie che vorresti insegnare</li>
                    <li>Le tue qualifiche e certificazioni</li>
                    <li>Perché vuoi insegnare sulla nostra piattaforma</li>
                  </ul>
                </div>

                <Button type="submit" className="w-full" disabled={submitting || message.trim().length < 50}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    'Invia richiesta'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
